import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import CreateRoutes from "./routes";
import { setSiteSettingData } from "./store/slice/siteSettingSlice";
import { setPages } from "./store/slice/pageSlice";
import axios from "axios";
import BASE_URL from "./config/host";

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes client cache
// Bump this on each production deploy so old sessionStorage is ignored
const CACHE_VERSION = "2026-08-08-v3-noloader";

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { at, data, v } = JSON.parse(raw);
    if (v !== CACHE_VERSION) return null;
    if (!at || Date.now() - at > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function writeCache(key, data) {
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({ at: Date.now(), v: CACHE_VERSION, data }),
    );
  } catch {
    /* quota / private mode */
  }
}

/** Call after deploy if you need to force-clear homepage cache in this browser */
export function clearProntoPsyClientCache() {
  try {
    sessionStorage.removeItem("prontopsy_site_settings");
    sessionStorage.removeItem("prontopsy_pages");
  } catch {
    /* ignore */
  }
}

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    // Non-blocking icon CSS
    import("./assets/libs/FontAwesome_v6.5.2/css/all.css");
    import("./assets/libs/FontAwesome_v6.5.2/css/sharp-regular.css");
    import("./assets/libs/FontAwesome_v6.5.2/css/sharp-solid.css");

    const cachedSettings = readCache("prontopsy_site_settings");
    const cachedPages = readCache("prontopsy_pages");
    if (cachedSettings) dispatch(setSiteSettingData(cachedSettings));
    if (cachedPages) dispatch(setPages(cachedPages));

    const fetchSiteSettingData = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/site-settings`, {
          timeout: 12000,
        });
        const payload = response?.data?.data;
        if (payload) {
          dispatch(setSiteSettingData(payload));
          writeCache("prontopsy_site_settings", payload);
        }
      } catch (error) {
        console.log("Error loading site settings:", error);
      }
    };

    const fetchPageData = async () => {
      try {
        // Lower limit + only fields needed for marketing pages (faster payload)
        const response = await axios.get(`${BASE_URL}/page/list`, {
          params: { limit: 50 },
          timeout: 15000,
        });
        const pages = response?.data?.docs?.data;
        if (pages) {
          dispatch(setPages(pages));
          writeCache("prontopsy_pages", pages);
        }
      } catch (error) {
        console.log("Error loading page list:", error);
      }
    };

    // Refresh in background (UI already has cache if available)
    Promise.all([fetchSiteSettingData(), fetchPageData()]);
  }, [dispatch]);

  return <CreateRoutes />;
}

export default App;
